export type PanelId = 'adjust' | 'beauty' | 'layers' | 'metadata' | 'ai' | 'export';
export type ToolId = 'move' | 'crop' | 'brush' | 'heal' | 'liquify' | 'text' | 'compare';

export interface Point { x: number; y: number; pressure: number }
export interface Stroke { id: string; points: Point[]; color: string; size: number; opacity: number; erase?: boolean }
export interface LiquifyStroke { id: string; points: Point[]; radius: number; strength: number }
export interface CropRect { x: number; y: number; width: number; height: number }
export interface ToolSettings {
  brushSize: number;
  brushColor: string;
  brushOpacity: number;
  brushErase: boolean;
  liquifySize: number;
  liquifyStrength: number;
}

export interface Adjustments {
  exposure: number;
  contrast: number;
  highlights: number;
  shadows: number;
  temperature: number;
  tint: number;
  saturation: number;
  vibrance: number;
  hue: number;
  clarity: number;
  dehaze: number;
  sharpen: number;
  noise: number;
  vignette: number;
  grain: number;
  blur: number;
  grayscale: number;
  sepia: number;
}

export interface BeautyAdjustments {
  auto: number;
  smooth: number;
  tone: number;
  blemish: number;
  oil: number;
  darkCircle: number;
  whitenTeeth: number;
  eyeLight: number;
  slimFace: number;
  jaw: number;
  chin: number;
  nose: number;
  eyes: number;
  lips: number;
  makeup: number;
  backgroundBlur: number;
}

export interface ImageMetadata {
  capturedAt: string;
  timezone: string;
  latitude: string;
  longitude: string;
  altitude: string;
  title: string;
  description: string;
  author: string;
  copyright: string;
  keywords: string;
  camera: string;
  lens: string;
}

export interface Layer {
  id: string;
  name: string;
  type: 'image' | 'paint' | 'text' | 'ai';
  visible: boolean;
  locked: boolean;
  opacity: number;
  blendMode: GlobalCompositeOperation;
  source?: string;
  text?: string;
  color?: string;
  fontSize?: number;
  x?: number;
  y?: number;
}

export interface EditorDocument {
  id: string;
  name: string;
  width: number;
  height: number;
  createdAt: string;
  updatedAt: string;
  sourceName: string;
  sourceType: string;
  sourceBytes: number;
  layers: Layer[];
  strokes: Stroke[];
  liquifyStrokes: LiquifyStroke[];
  adjustments: Adjustments;
  beauty: BeautyAdjustments;
  metadata: ImageMetadata;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
  cropAspect: string;
  cropRect: CropRect;
  faceStatus: 'idle' | 'detecting' | 'ready' | 'unavailable';
  faceBoxes: Array<{ x: number; y: number; width: number; height: number }>;
}

export interface ExportPreset {
  format: 'image/jpeg' | 'image/png' | 'image/webp';
  quality: number;
  scale: number;
  maxEdge: number;
  targetKB: number;
  metadataPolicy: 'all' | 'copyright' | 'no-location' | 'none';
}

export type AIProvider = 'openai' | 'gemini' | 'stability' | 'replicate' | 'fal' | 'custom';
export interface AIEndpointProfile {
  provider: AIProvider;
  name: string;
  url: string;
  apiKey: string;
  method: 'POST' | 'PUT';
  transport: 'direct' | 'proxy';
  proxyUrl: string;
  authHeader: string;
  requestTemplate: string;
  responsePath: string;
}

export const defaultAdjustments: Adjustments = {
  exposure: 0, contrast: 0, highlights: 0, shadows: 0, temperature: 0, tint: 0,
  saturation: 0, vibrance: 0, hue: 0, clarity: 0, dehaze: 0, sharpen: 0,
  noise: 0, vignette: 0, grain: 0, blur: 0, grayscale: 0, sepia: 0
};

export const defaultBeauty: BeautyAdjustments = {
  auto: 0, smooth: 0, tone: 0, blemish: 0, oil: 0, darkCircle: 0,
  whitenTeeth: 0, eyeLight: 0, slimFace: 0, jaw: 0, chin: 0, nose: 0,
  eyes: 0, lips: 0, makeup: 0, backgroundBlur: 0
};

export const defaultMetadata: ImageMetadata = {
  capturedAt: '', timezone: '+08:00', latitude: '', longitude: '', altitude: '',
  title: '', description: '', author: '', copyright: '', keywords: '', camera: '', lens: ''
};

export const defaultToolSettings: ToolSettings = {
  brushSize: 28,
  brushColor: '#c7f36b',
  brushOpacity: 90,
  brushErase: false,
  liquifySize: 120,
  liquifyStrength: 55
};
